import type { SendBatch } from '@/components/contentPane/composerMessage';

/**
 * One send queue per channel, living at module level, and a hand-back box
 * for sends that failed (U6, review of #373).
 *
 * - Order: sends of one channel run one after another, whatever Composer
 *   instance queued them — leaving the channel and coming back (A→B→A)
 *   cannot let a new message overtake one still waiting.
 * - A failed message stops the line: it and every message queued after it
 *   in that channel are rolled back (bubbles removed) and handed back
 *   together, in send order, so nothing arrives out of order.
 * - Hand-back goes through `failedSends` keyed by channel: the Composer of
 *   that channel takes it as soon as it is mounted (immediately if it
 *   already is), so a remount never loses the text or the attachments.
 *
 * Pure / dependency-injected: `createChannelSendQueue` is unit-tested; the
 * app uses one instance wired to the data store (see Composer).
 */

/** One optimistic bubble already painted, plus its idempotency key. */
export interface QueuedBatch<A> extends SendBatch<A> {
  tmpId: string;
  /** clientNonce: the server returns the original message for a repeat. */
  nonce: string;
}

/** What one failed message (or the part of it not yet sent) gives back. */
export interface FailedPart<A, S, R> {
  content: string;
  /** Batches that never reached the server (for a nonce-safe retry). */
  batches: QueuedBatch<A>[];
  /** Tray slots of those batches' attachments, if they can come back. */
  slots: S[];
  replyTo: R | null;
  grouped: boolean;
}

/** A failed send, merged in send order, waiting for its channel's Composer. */
export type FailedSend<A, S, R> = FailedPart<A, S, R>;

export interface MessageJob<A, S, R, M> {
  kind: 'message';
  batches: QueuedBatch<A>[];
  /** Slots that left the tray with this send; released once all are sent. */
  slots: S[];
  /** Which slots belong to which attachment (to give back only unsent ones). */
  slotFor: (att: A) => S | undefined;
  replyTo: R | null;
  grouped: boolean;
  send: (batch: QueuedBatch<A>) => Promise<M>;
}

export interface TaskJob {
  kind: 'task';
  /** Resolving `false` means this send failed: the line stops behind it. */
  run: () => Promise<void | boolean>;
  /**
   * Called instead of `run` when an earlier send of the line failed (in
   * send order), so the task can hand its content back. Tasks without it
   * still run after a failure.
   */
  cancel?: () => void;
}

export type SendJob<A, S, R, M> = MessageJob<A, S, R, M> | TaskJob;

export interface QueueDeps<A, S, R, M> {
  confirm: (channelId: string, tmpId: string, real: M) => void;
  rollback: (channelId: string, tmpId: string) => void;
  /** Free thumbnails of slots whose message reached the server. */
  release: (slots: S[]) => void;
  onError: (err: unknown) => void;
  handBack: (channelId: string, failed: FailedSend<A, S, R>) => void;
}

/** Merge parts (oldest first) into one hand-back. */
export function mergeFailedParts<A, S, R>(parts: FailedPart<A, S, R>[]): FailedSend<A, S, R> {
  return {
    content: parts.map((p) => p.content).filter(Boolean).join('\n'),
    batches: parts.flatMap((p) => p.batches),
    slots: parts.flatMap((p) => p.slots),
    replyTo: parts.find((p) => p.replyTo !== null)?.replyTo ?? null,
    grouped: parts[0]?.grouped ?? true,
  };
}

function partOf<A, S, R, M>(job: MessageJob<A, S, R, M>, from: number): FailedPart<A, S, R> {
  const batches = job.batches.slice(from);
  const slots: S[] = [];
  for (const b of batches) {
    for (const a of b.atts) {
      const slot = job.slotFor(a);
      if (slot !== undefined) slots.push(slot);
    }
  }
  return {
    content: batches.map((b) => b.content).filter(Boolean).join('\n'),
    batches,
    slots,
    replyTo: batches.some((b) => b.withReply) ? job.replyTo : null,
    grouped: job.grouped,
  };
}

export function createChannelSendQueue<A, S, R, M>(deps: QueueDeps<A, S, R, M>) {
  const lines = new Map<string, { jobs: SendJob<A, S, R, M>[]; running: boolean; idle: Promise<void> }>();
  const activeLines = new Set<string>();
  const activityListeners = new Set<() => void>();
  const setActive = (channelId: string, active: boolean) => {
    const wasBusy = activeLines.size > 0;
    if (active) activeLines.add(channelId); else activeLines.delete(channelId);
    if (wasBusy !== (activeLines.size > 0)) for (const listener of activityListeners) listener();
  };

  async function deliver(job: MessageJob<A, S, R, M>, channelId: string): Promise<number> {
    for (let i = 0; i < job.batches.length; i += 1) {
      const batch = job.batches[i]!;
      try {
        const real = await job.send(batch);
        deps.confirm(channelId, batch.tmpId, real);
      } catch (err) {
        deps.onError(err);
        return i;
      }
    }
    return -1;
  }

  /** Free thumbnails of the job's slots that are not coming back (sent, or
   *  a failed upload that never became an attachment). */
  function releaseSent(job: MessageJob<A, S, R, M>, part: FailedPart<A, S, R>): void {
    const back = new Set(part.slots);
    deps.release(job.slots.filter((s) => !back.has(s)));
  }

  /** Roll back and hand back every message still waiting behind a failure;
   *  tasks that can hand their content back are cancelled in order. */
  function stopLine(
    channelId: string,
    line: { jobs: SendJob<A, S, R, M>[] },
    parts: FailedPart<A, S, R>[],
  ): void {
    const rest: SendJob<A, S, R, M>[] = [];
    for (const later of line.jobs) {
      if (later.kind === 'task') {
        if (later.cancel) later.cancel();
        else rest.push(later);
        continue;
      }
      for (const b of later.batches) deps.rollback(channelId, b.tmpId);
      const part = partOf(later, 0);
      releaseSent(later, part);
      parts.push(part);
    }
    line.jobs = rest;
    if (parts.length > 0) deps.handBack(channelId, mergeFailedParts(parts));
  }

  async function drain(channelId: string): Promise<void> {
    const line = lines.get(channelId);
    if (!line || line.running) return;
    line.running = true;
    setActive(channelId, true);
    try {
      while (line.jobs.length > 0) {
        const job = line.jobs.shift()!;
        if (job.kind === 'task') {
          let ok: void | boolean = true;
          try {
            ok = await job.run();
          } catch (err) {
            deps.onError(err);
          }
          if (ok === false) stopLine(channelId, line, []);
          continue;
        }
        const failedAt = await deliver(job, channelId);
        if (failedAt < 0) {
          deps.release(job.slots);
          continue;
        }
        // Stop the line: this message's unsent part and every message
        // queued after it come back together, in order.
        const part = partOf(job, failedAt);
        releaseSent(job, part);
        for (const b of part.batches) deps.rollback(channelId, b.tmpId);
        stopLine(channelId, line, [part]);
      }
    } finally {
      line.running = false;
      setActive(channelId, false);
    }
  }

  return {
    /** Queue a send for `channelId`; resolves once the queue has run it. */
    enqueue(channelId: string, job: SendJob<A, S, R, M>): Promise<void> {
      let line = lines.get(channelId);
      if (!line) {
        line = { jobs: [], running: false, idle: Promise.resolve() };
        lines.set(channelId, line);
      }
      line.jobs.push(job);
      if (!line.running) line.idle = drain(channelId);
      return line.idle;
    },
    /** Aggregate activity, without walking every channel in the account. */
    hasPending(): boolean { return activeLines.size > 0; },
    subscribeActivity(listener: () => void): () => void {
      activityListeners.add(listener);
      return () => { activityListeners.delete(listener); };
    },
    /** Jobs waiting or running in a channel (tests, diagnostics). */
    pending(channelId: string): number {
      const line = lines.get(channelId);
      return line ? line.jobs.length + (line.running ? 1 : 0) : 0;
    },
  };
}

/**
 * Hand-back box: failed sends wait here, per channel, until a Composer of
 * that channel takes them.
 */
export function createFailedSendBox<T>() {
  const box = new Map<string, T[]>();
  const listeners = new Set<(channelId: string) => void>();
  const activityListeners = new Set<() => void>();
  return {
    put(channelId: string, item: T): void {
      box.set(channelId, [...(box.get(channelId) ?? []), item]);
      for (const l of listeners) l(channelId);
      for (const listener of activityListeners) listener();
    },
    /** Take (and remove) everything waiting for `channelId`, oldest first. */
    take(channelId: string): T[] {
      const items = box.get(channelId) ?? [];
      box.delete(channelId);
      if (items.length > 0) for (const listener of activityListeners) listener();
      return items;
    },
    hasItems(): boolean { return box.size > 0; },
    subscribeActivity(listener: () => void): () => void {
      activityListeners.add(listener);
      return () => { activityListeners.delete(listener); };
    },
    /** Channels with something waiting. */
    keys(): string[] {
      return [...box.keys()];
    },
    subscribe(listener: (channelId: string) => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/**
 * Put the failed text back without clobbering what the user typed since:
 * empty field → the failed text; otherwise the failed text goes first (it
 * was sent earlier), on its own line.
 */
export function mergeRestoredDraft(current: string, restored: string, sep = '\n'): string {
  if (!restored) return current;
  if (!current.trim()) return restored;
  return `${restored}${sep}${current}`;
}

/**
 * Put a failed send back after the ones already given back in the same run,
 * so two failures read in send order («c1\nc2», not «c2\nc1»). `restored`
 * is what earlier hand-backs of this run put at the top of the field (null
 * when none); it is only extended while the field still starts with it.
 * `sep` joins the parts (a space for a one-line field).
 */
export function restoreInOrder(
  current: string,
  restored: string | null,
  content: string,
  sep = '\n',
): { draft: string; restored: string | null } {
  if (!content) return { draft: current, restored };
  if (restored && current.startsWith(restored)) {
    const next = `${restored}${sep}${content}`;
    return { draft: next + current.slice(restored.length), restored: next };
  }
  return { draft: mergeRestoredDraft(current, content, sep), restored: content };
}

/**
 * Reuse a failed batch's clientNonce only for an unchanged resend: same
 * text and the same attachments. If the "failed" send in fact reached the
 * server (answer lost), the server returns that message instead of creating
 * a second one. Anything edited is a new message with a new nonce.
 */
export function retryNonce(
  retry: { content: string; uploadIds: string[]; nonce: string } | null,
  content: string,
  uploadIds: string[],
): string | null {
  if (!retry) return null;
  if (retry.content !== content) return null;
  if (retry.uploadIds.length !== uploadIds.length) return null;
  return retry.uploadIds.every((id, i) => id === uploadIds[i]) ? retry.nonce : null;
}
